import { usePreventRemove } from '@react-navigation/native';
import { Stack, useFocusEffect, useNavigation, useRouter } from 'expo-router';
import { useCallback, useMemo, useRef } from 'react';
import { ScrollViewProps, StyleSheet, View } from 'react-native';

import {
  AnchorSectionList,
  Button,
  HeaderFrame,
  HeaderIconButton,
  IconChevronLeft,
  IconClose,
  Text,
  YStack,
  createMqStyles,
  tokens,
  useMqSelect,
} from '@fhs/ui';

import { CustomizationGroup } from '../components/customization-group';
import { useConfirmUnsavedCustomizationModal } from '../components/use-confirm-unsaved-customization-modal';
import { useMenuPicker } from '../state/menu-picker';
import { CustomizationSelections } from '../types';
export { ErrorBoundary } from '@fhs/routing/src/components/page-dev-error-boundary';
const listItemProps = { titleFontWeight: 'normal' } as const;
export function ScreenCustomizeItem(props: {
  renderScrollComponent?: React.ComponentType<ScrollViewProps> | React.FC<ScrollViewProps>;
}) {
  const { dismiss } = useRouter();
  const navigation = useNavigation();
  const isDesktop = useMqSelect({ $gteDesktop: true }, false);
  const mqStyles = useMqStyles();
  const menuPicker = useMenuPicker();
  const {
    hasUnsavedCustomizationSelections,
    selectedPickerAspectOption,
    draftCustomizationSelections,
    setUserCustomizationSelections,
    userCustomizationSelections,
    setShouldShowCustomizationScreen,
    keepCustomizationInView,
  } = menuPicker;

  const close = useCallback(() => {
    if (keepCustomizationInView) {
      setShouldShowCustomizationScreen(false);
    } else {
      dismiss(1);
    }
  }, [keepCustomizationInView, setShouldShowCustomizationScreen, dismiss]);

  const isBackAlreadyHandled = useRef(false);
  const { confirmationDialog, openConfirmationDialog } = useConfirmUnsavedCustomizationModal({
    onOpen: useCallback(() => {
      isBackAlreadyHandled.current = true;
    }, []),
    onClose: useCallback(() => {
      close();
    }, [close]),
  });
  const handleSave = useCallback(() => {
    isBackAlreadyHandled.current = true;
    setUserCustomizationSelections({
      ...userCustomizationSelections,
      ...draftCustomizationSelections,
    });
    close();
  }, [
    draftCustomizationSelections,
    close,
    setUserCustomizationSelections,
    userCustomizationSelections,
  ]);
  const handleClose = () => {
    isBackAlreadyHandled.current = true;
    if (hasUnsavedCustomizationSelections) {
      openConfirmationDialog();
    } else {
      close();
    }
  };
  usePreventRemove(hasUnsavedCustomizationSelections, ({ data }) => {
    if (isBackAlreadyHandled.current) {
      navigation.dispatch(data.action);
    } else {
      openConfirmationDialog();
    }
  });

  useFocusEffect(
    useCallback(() => {
      isBackAlreadyHandled.current = false;
    }, [])
  );
  const displayGroups = selectedPickerAspectOption?.item?.customizations?.displayGroups ?? [];
  const allCustomizations = displayGroups.filter(
    customization => !customization.displayGroup.showOnProductPage
  );
  const sections = useMemo(() => {
    const mergedSelections: CustomizationSelections = {
      ...menuPicker.resolvedCustomizationSelections,
      ...menuPicker.draftCustomizationSelections,
    };
    return allCustomizations.map(group => ({
      id: group.displayGroup.id,
      title: '',
      anchorTitle: group.displayGroup.displayName,
      image: {
        asset: { uri: '', blurHash: '' },
        altText: group.displayGroup.displayName,
      },
      items: (
        <View style={styles.groupWrapper}>
          <View style={styles.innerGroup}>
            <CustomizationGroup
              borderless
              listItemProps={listItemProps}
              customizationGroup={group}
              customizationSelections={mergedSelections}
              setCustomizationSelections={menuPicker.setDraftCustomizationSelections}
            />
          </View>
        </View>
      ),
    }));
  }, [
    allCustomizations,
    menuPicker.draftCustomizationSelections,
    menuPicker.resolvedCustomizationSelections,
    menuPicker.setDraftCustomizationSelections,
  ]);

  return (
    <View style={styles.screen}>
      <Stack.Screen
        options={{
          header: () => (
            <HeaderFrame style={mqStyles.headerFrame}>
              {!isDesktop && (
                <HeaderFrame.Left>
                  <HeaderIconButton
                    accessibilityLabel="Exit customization"
                    accessibilityHint="Returns to the item page. You may be asked to save or discard changes."
                    aria-label="Exit customization"
                    onPress={handleClose}
                  >
                    <IconChevronLeft
                      accessible={false}
                      accessibilityElementsHidden
                      importantForAccessibility="no"
                    />
                  </HeaderIconButton>
                </HeaderFrame.Left>
              )}
              <HeaderFrame.Middle>
                <YStack style={mqStyles.headerTitleStack}>
                  <Text.Heading type={isDesktop ? 'one' : 'four'}>Customize Item</Text.Heading>
                  <Text.Paragraph size={isDesktop ? 'md' : 'sm'}>
                    {[
                      selectedPickerAspectOption?.displayName,
                      selectedPickerAspectOption?.item?.baseCalories &&
                        `${selectedPickerAspectOption?.item?.baseCalories} Cal`,
                    ]
                      .filter(Boolean)
                      .join(' • ')}
                  </Text.Paragraph>
                </YStack>
              </HeaderFrame.Middle>
              {isDesktop && (
                <Button
                  type="outline-subtle"
                  size="xl"
                  onPress={handleClose}
                  style={styles.desktopCloseButton}
                  accessibilityLabel="Exit customization"
                  accessibilityHint="Returns to the item page. You may be asked to save or discard changes."
                  aria-label="Exit customization"
                >
                  <Button.Icon>
                    <IconClose
                      accessible={false}
                      accessibilityElementsHidden
                      importantForAccessibility="no"
                    />
                  </Button.Icon>
                </Button>
              )}
            </HeaderFrame>
          ),
        }}
      />
      <AnchorSectionList sections={sections}>
        <View style={styles.anchorListContainer}>
          <AnchorSectionList.AnchorListHorizontal
            contentContainerStyle={mqStyles.anchorListContent}
          />
        </View>

        <AnchorSectionList.SectionList renderScrollComponent={props.renderScrollComponent} />
        <View style={styles.screenFooter}>
          <View style={styles.screenFooterContent}>
            <Button
              size="xl"
              style={styles.looksGoodButton}
              onPress={handleSave}
              testID="customization-submit-button"
            >
              <Button.Text>Looks Good!</Button.Text>
            </Button>
          </View>
        </View>
        {confirmationDialog}
      </AnchorSectionList>
    </View>
  );
}

const MAX_CONTENT_WIDTH = 480;
const useMqStyles = createMqStyles({
  headerFrame: {
    $gteDesktop: {
      minHeight: 132,
    },
  },
  headerTitleStack: {
    $base: {
      backgroundColor: tokens.colors.$white,
      alignItems: 'center',
      gap: 4,
    },
    $gteDesktop: {
      paddingVertical: 16,
    },
  },
  anchorListContent: {
    $gteDesktop: {
      flexDirection: 'row',
      justifyContent: 'flex-start',
      paddingVertical: 8,
      paddingHorizontal: 16,
      width: '100%',
    },
  },
});
const styles = StyleSheet.create({
  screen: {
    flexGrow: 1,
    flexShrink: 1,
    overflow: 'hidden',
    width: '100%',
  },
  groupWrapper: {
    width: '100%',
    alignItems: 'center',
  },
  innerGroup: {
    width: '100%',
    maxWidth: MAX_CONTENT_WIDTH,
    alignSelf: 'center',
  },

  anchorListContainer: {
    width: '100%',
    backgroundColor: tokens.colors.$white,
  },
  anchorListContent: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    paddingVertical: 8,
    paddingHorizontal: 16,
    width: '100%',
    backgroundColor: tokens.colors.$white,
  },
  screenFooter: {
    backgroundColor: tokens.colors.$white,
    borderTopColor: tokens.colors.$black10,
    borderTopWidth: 1,
  },
  screenFooterContent: {
    padding: 16,
    width: '100%',
    maxWidth: MAX_CONTENT_WIDTH,
    marginHorizontal: 'auto',
    alignItems: 'center',
  },
  looksGoodButton: {
    width: '100%',
  },
  desktopCloseButton: {
    position: 'absolute',
    top: 20,
    right: 32,
  },
});
